import { useState } from 'react'
import type { CSSProperties } from 'react'
import { useT } from '../../i18n'
import { RoomQR } from '../../components/RoomQR'
import { publicOrigin } from '../../lib/api/tv'
import type { WordTvSeat } from '../../lib/api/wordTv'

/** Pass-and-play stays a one-phone mode — 8 is the practical ceiling. */
const MAX_PASS = 8
/** Rooms go bigger: everyone plays their own phone in parallel. */
const MAX_ROOM = 16

export type SetupMode = 'choose' | 'pass' | 'create' | 'join'

interface Props {
  accent: string
  border: string
  /** btn-glass-fakeout | btn-glass-closeenough — the game's tinted glass. */
  glassClass: string
  /** Card art for the create/join sub-screens. */
  gameImage: string
  busy: boolean
  /** Controlled by the parent screen, so the header back button can
   *  return a sub-screen to the chooser instead of leaving the game. */
  mode: SetupMode
  onModeChange: (mode: SetupMode) => void
  /** Empty array = solo. Otherwise one entry per named seat, in turn order. */
  onStart: (names: string[]) => void
  /** Open a private room (subscriber-gated by the caller). */
  onHostRoom: (seats: number, rounds: number, coop: boolean) => void
  /** Offer the co-op mode picker (CLOSE ENOUGH only). */
  coopChoice?: boolean
  /** Claim a seat in someone else's room. Free — no subscription. */
  onJoinRoom: (code: string) => void
  onPlayWeekly: () => void
}

/**
 * Endless setup, matching the other games' menus: a "Play with friends"
 * card list (party room, join by code, pass play, weekly) with solo as
 * the floating primary action. No AI opponents and no online match on
 * the word games, ever — rooms are private, code-only tables.
 */
export function EndlessSetup({
  accent, border, glassClass, gameImage, busy, mode, onModeChange,
  onStart, onHostRoom, onJoinRoom, onPlayWeekly, coopChoice,
}: Props) {
  const t = useT()
  const setMode = onModeChange
  const [names, setNames] = useState<string[]>(Array(MAX_PASS).fill(''))
  const [seats, setSeats] = useState(4)
  const [rounds, setRounds] = useState(1)
  const [coop, setCoop] = useState(false)
  const [code, setCode] = useState('')

  const seated = names.map(n => n.trim()).filter(Boolean)
  const canPass = seated.length >= 2

  function setName(i: number, v: string) {
    setNames(prev => prev.map((n, j) => (j === i ? v : n)))
  }

  const bigGlass = `${glassClass} w-full py-4 text-center font-display font-bold text-xl
                    active:scale-[0.98] transition-transform disabled:opacity-40`
  const backGlass = `${glassClass} w-full mt-3 py-3.5 text-center font-display font-bold text-lg
                     active:scale-[0.98] transition-transform`

  // ─── Create a room ───────────────────────────────────────────────────
  if (mode === 'create') {
    return (
      <div className="mt-2">
        <div className="text-center">
          <img src={gameImage} alt=""
               className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4 shadow-card" />
          <h2 className="font-display text-xl font-bold text-brand-text">{t('New room')}</h2>
          <p className="text-brand-muted text-sm mt-1">{t("You'll get a 6-character code to share.")}</p>
        </div>

        {/* No toggle here on purpose: word rooms are always private —
            there is no public flag and no matchmaking path to find one. */}
        <div className="card p-4 mt-5">
          <p className="font-semibold text-brand-text">{t('Private room')}</p>
          <p className="text-sm text-brand-muted mt-0.5">
            {t('Only people with the code can join.')}
          </p>
        </div>

        {coopChoice && (
          <>
            <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mt-5 mb-2">
              {t('Mode')}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {([false, true] as const).map(isCoop => (
                <button
                  key={String(isCoop)}
                  type="button"
                  onClick={() => setCoop(isCoop)}
                  className="py-3 rounded-2xl border-2 font-display font-bold text-lg
                             bg-white active:scale-95 transition-transform"
                  style={coop === isCoop
                    ? { borderColor: accent, color: accent }
                    : { borderColor: '#E5E7EB', color: '#6B7280' }}
                >
                  {isCoop ? t('Co-op') : t('Party')}
                </button>
              ))}
            </div>
            <p className="text-xs text-brand-muted mt-2">
              {coop
                ? t('One shared board. Guess together, no hints. Every wrong guess costs the team 5 points.')
                : t('Everyone races the same word on their own board.')}
            </p>
          </>
        )}

        <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mt-5 mb-2">
          {t('Rounds')}
        </p>
        <div className="grid grid-cols-3 gap-2">
          {[1, 2, 3].map(n => (
            <button
              key={n}
              type="button"
              onClick={() => setRounds(n)}
              className="py-3 rounded-2xl border-2 font-display font-bold text-lg
                         bg-white active:scale-95 transition-transform"
              style={n === rounds
                ? { borderColor: accent, color: accent }
                : { borderColor: '#E5E7EB', color: '#6B7280' }}
            >
              {n}
            </button>
          ))}
        </div>
        <p className="text-xs text-brand-muted mt-2">
          {t('Scores add up across rounds. Everyone finishes a round before the next one starts.')}
        </p>

        <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mt-5 mb-2">
          {t('Seats')}
        </p>
        <div className="grid grid-cols-5 gap-2">
          {Array.from({ length: MAX_ROOM - 1 }, (_, i) => i + 2).map(n => (
            <button
              key={n}
              type="button"
              onClick={() => setSeats(n)}
              className="py-3 rounded-2xl border-2 font-display font-bold text-lg
                         bg-white active:scale-95 transition-transform"
              style={n === seats
                ? { borderColor: accent, color: accent }
                : { borderColor: '#E5E7EB', color: '#6B7280' }}
            >
              {n}
            </button>
          ))}
        </div>
        <p className="text-xs text-brand-muted mt-2">
          {t('Seats can’t be added later, so leave room for stragglers.')}
        </p>

        <button
          type="button"
          disabled={busy}
          onClick={() => onHostRoom(seats, rounds, coopChoice ? coop : false)}
          className={`${bigGlass} mt-5`}
        >
          {busy ? t('Starting…') : t('Open a room')}
        </button>
        <button type="button" onClick={() => setMode('choose')}
                className={backGlass} style={{ color: accent }}>
          {t('← Back')}
        </button>
      </div>
    )
  }

  // ─── Join a room ─────────────────────────────────────────────────────
  if (mode === 'join') {
    return (
      <div className="mt-2">
        <div className="text-center">
          <img src={gameImage} alt=""
               className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4 shadow-card" />
          <h2 className="font-display text-xl font-bold text-brand-text">{t('Enter room code')}</h2>
          <p className="text-brand-muted text-sm mt-1">{t('Ask your host for the 6-character code')}</p>
        </div>
        <input
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
          placeholder="ABC123"
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          className="w-full mt-5 rounded-2xl border-2 border-brand-border bg-white px-4 py-4
                     text-center font-display font-bold text-2xl tracking-[0.3em]
                     text-brand-text placeholder:text-brand-muted/40 focus:outline-none"
          style={code.length === 6 ? { borderColor: border } : undefined}
        />
        <button
          type="button"
          disabled={busy || code.length !== 6}
          onClick={() => onJoinRoom(code)}
          className={`${bigGlass} mt-4`}
        >
          {busy ? t('Joining…') : t('Join Room')}
        </button>
        <button type="button" onClick={() => setMode('choose')}
                className={backGlass} style={{ color: accent }}>
          {t('← Back')}
        </button>
      </div>
    )
  }

  // ─── Pass-and-play ───────────────────────────────────────────────────
  if (mode === 'pass') {
    return (
      <div className="mt-2">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
          {t('Phone pass-and-play')}
        </p>
        <p className="text-xs text-brand-muted mt-1.5 mb-3 leading-relaxed">
          {t('Add everyone playing. The phone goes around and each person answers the same words, then the highest score wins.')}
        </p>

        <div className="space-y-2">
          {names.map((n, i) => (
            <div key={i} className="flex items-center gap-2.5">
              <span
                className="w-7 h-7 shrink-0 rounded-full border-2 flex items-center justify-center
                           font-display font-bold text-xs"
                style={{ borderColor: border, color: accent }}
              >
                {i + 1}
              </span>
              <input
                value={n}
                onChange={e => setName(i, e.target.value)}
                maxLength={20}
                placeholder={t('Player {n}', { n: i + 1 })}
                className="flex-1 min-w-0 rounded-2xl border-2 border-brand-border bg-white
                           px-3.5 py-2.5 text-sm text-brand-text
                           placeholder:text-brand-muted/60 focus:outline-none"
                style={n.trim() ? { borderColor: border } : undefined}
              />
            </div>
          ))}
        </div>

        <button
          type="button"
          disabled={busy || !canPass}
          onClick={() => onStart(seated)}
          className={`${bigGlass} mt-5`}
        >
          {busy ? t('Starting…') : canPass ? t('START GAME') : t('Add 2 or more names')}
        </button>
        <button type="button" onClick={() => setMode('choose')}
                className={backGlass} style={{ color: accent }}>
          {t('← Back')}
        </button>
      </div>
    )
  }

  // ─── Choose (the standard menu) ──────────────────────────────────────
  const cardBtn = `card w-full p-4 flex items-center gap-3 text-left
                   active:bg-brand-elevated transition-colors disabled:opacity-60`
  return (
    <div className="mt-2 space-y-2">
      <p className="text-xs font-medium text-brand-muted uppercase tracking-wide">
        {t('Play with friends')}
      </p>

      <button type="button" onClick={() => setMode('create')} disabled={busy} className={cardBtn}>
        <span className="text-2xl shrink-0">🎉</span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-brand-text">{t('Party play with friends')}</p>
          <p className="text-xs text-brand-muted">
            {t('Host a private room for up to 16 players. Share a 6-character code.')}
          </p>
        </div>
      </button>

      <button type="button" onClick={() => setMode('join')} disabled={busy} className={cardBtn}>
        <span className="text-2xl shrink-0">🔑</span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-brand-text">{t('Join a room')}</p>
          <p className="text-xs text-brand-muted">{t('Enter a 6-character code from a friend.')}</p>
        </div>
      </button>

      <button type="button" onClick={() => setMode('pass')} disabled={busy} className={cardBtn}>
        <span className="text-2xl shrink-0">📱</span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-brand-text">{t('Phone Pass Play')}</p>
          <p className="text-xs text-brand-muted">
            {t('Local only. Pass the phone between people in the room. No internet needed.')}
          </p>
        </div>
      </button>

      <button type="button" onClick={onPlayWeekly} disabled={busy} className={cardBtn}>
        <span className="text-2xl shrink-0">📆</span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-brand-text">{t('Play the weekly')}</p>
          <p className="text-xs text-brand-muted">{t('The same words for the whole community.')}</p>
        </div>
      </button>

      {/* Clearance for the floating solo CTA below. */}
      <div className="h-24" aria-hidden="true" />

      <div className="floating-actions">
        {/* Width and type come from the class — the floating-actions
            glass recipe only paints the surface, so a bare button
            renders as a content-sized chip in the corner. The inline
            vars re-point the glass tint to this game's colour. */}
        <button
          type="button"
          disabled={busy}
          onClick={() => onStart([])}
          className="w-full py-4 font-display font-bold text-xl disabled:opacity-40"
          style={{ '--cta-tint': border, '--cta-pct': '30%' } as CSSProperties}
        >
          {busy ? t('Starting…') : t('Play solo')}
        </button>
      </div>
    </div>
  )
}

/** Between-player handoff card for pass-and-play. */
export function PassDevice({
  name, accent, border, onReady,
}: { name: string; accent: string; border: string; onReady: () => void }) {
  const t = useT()
  return (
    <div className="card p-6 mt-2 rounded-2xl text-center border-2"
         style={{ borderColor: border }}>
      <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
        {t('Pass the phone')}
      </p>
      <p className="font-display font-bold text-3xl mt-1" style={{ color: accent }}>
        {name}
      </p>
      <p className="text-sm text-brand-muted mt-2">
        {t('Don’t look until it’s your turn.')}
      </p>
      <button
        type="button"
        onClick={onReady}
        className="w-full mt-5 py-3.5 rounded-2xl font-display font-bold text-lg
                   bg-brand-text text-white active:scale-[0.98] transition-transform"
      >
        {t('I’m ready')}
      </button>
    </div>
  )
}

/**
 * End-of-game actions, stacked. Both glass-tinted in the game's colour
 * so the finish reads as part of the game rather than app chrome.
 */
export function EndlessOutro({
  glassClass, accent, onPlayAgain, onBackToGames,
}: {
  glassClass: string
  /** Ink for the secondary action, so the pair reads primary/secondary. */
  accent: string
  onPlayAgain: () => void
  onBackToGames: () => void
}) {
  const t = useT()
  return (
    <div className="mt-5 space-y-3">
      <button
        type="button"
        onClick={onPlayAgain}
        className={`${glassClass} w-full py-4 text-center font-display font-bold text-xl
                    active:scale-[0.98] transition-transform`}
      >
        {t('Play again')}
      </button>
      <button
        type="button"
        onClick={onBackToGames}
        className={`${glassClass} w-full py-4 text-center font-display font-bold text-xl
                    active:scale-[0.98] transition-transform`}
        style={{ color: accent }}
      >
        {t('Back to games')}
      </button>
    </div>
  )
}

/**
 * Room lobby: the code and QR to get everyone in, the URL to put on the
 * big screen, and the live roster. The host starts the game (which
 * fires the "room started" push to everyone seated); guests wait here
 * until the status flips. The PARENT polls the room and owns state —
 * this component just renders it.
 */
export function RoomLobby({
  code, game, accent, border, glassClass, isHost, seats, busy, leaveArmed, onStart, onLeave,
}: {
  code: string
  game: 'fake_out' | 'close_enough'
  accent: string
  border: string
  glassClass: string
  isHost: boolean
  seats: WordTvSeat[]
  busy: boolean
  /** Host back is a two-tap close; true = one more tap abandons. */
  leaveArmed?: boolean
  onStart: () => void
  onLeave: () => void
}) {
  const t = useT()
  const slug = game === 'close_enough' ? 'close-enough' : 'fake-out'
  const joined = seats.filter(s => s.joined)

  return (
    <div className="mt-2">
      <div className="card p-5 text-center border-2"
           style={{ borderColor: border }}>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
          {t('Room code')}
        </p>
        <p className="font-display font-black text-5xl tracking-[0.18em] mt-1"
           style={{ color: accent }}>
          {code}
        </p>
        <div className="mt-4 flex justify-center">
          <RoomQR code={code} size={148} />
        </div>
      </div>

      <div className="card p-4 mt-3 border-2 border-brand-border">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
          {t('Put this on the TV')}
        </p>
        <p className="font-mono text-sm font-bold text-brand-text mt-1 break-all">
          {publicOrigin().replace(/^https?:\/\//, '')}/tv/{slug}/{code}
        </p>
      </div>

      <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted mt-5 mb-2">
        {joined.length === 1
          ? t('1 player in')
          : t('{n} players in', { n: joined.length })}
      </p>
      <div className="space-y-2">
        {seats.map(s => (
          <div key={s.player}
               className={`flex items-center gap-3 rounded-2xl border-2 px-4 py-2.5 ${s.joined ? 'card' : ''}`}
               style={s.joined
                 ? { borderColor: border }
                 : { borderColor: '#E5E7EB', backgroundColor: 'transparent' }}>
            <span className="w-6 text-center font-display font-bold text-sm"
                  style={{ color: s.joined ? accent : '#9CA3AF' }}>
              {s.player + 1}
            </span>
            <span className={`flex-1 text-sm truncate ${s.joined ? 'font-semibold text-brand-text' : 'text-brand-muted'}`}>
              {s.joined ? s.name : t('Waiting…')}
            </span>
          </div>
        ))}
      </div>

      {isHost ? (
        <>
          <button
            type="button"
            disabled={busy}
            onClick={onStart}
            className={`${glassClass} w-full mt-5 py-4 text-center font-display font-bold text-xl
                        active:scale-[0.98] transition-transform disabled:opacity-40`}
          >
            {busy ? t('Starting…') : t('START GAME')}
          </button>
          <p className="text-xs text-brand-muted text-center mt-2">
            {t('Latecomers can still join with the code while you play.')}
          </p>
        </>
      ) : (
        <p className="text-center text-brand-muted text-sm animate-pulse mt-5">
          {t('Waiting for the host to start…')}
        </p>
      )}

      <button
        type="button"
        onClick={onLeave}
        className={`${glassClass} w-full mt-3 py-3.5 text-center font-display font-bold text-lg
                    active:scale-[0.98] transition-transform`}
        style={{ color: accent }}
      >
        {isHost
          ? (leaveArmed ? t('Tap again to close the room') : t('← Back'))
          : t('Leave room')}
      </button>
    </div>
  )
}

/**
 * Final scoreboard for a multi-round room: one horizontal pill per
 * player, ranked, showing every round's points and the total.
 *
 * The room used to report a single cumulative number, so a three-round
 * game ended on an opaque "840" with no way to see who won which round.
 * Each round is its own chip, the total sits at the end, and first place
 * is on top.
 *
 * Only worth rendering when a room actually played more than one round;
 * for a single round the seat list above already says everything.
 */
export function RoundScoreboard({
  seats, accent, border, youPlayer,
}: {
  seats: WordTvSeat[]
  accent: string
  border: string
  youPlayer: number
}) {
  const t = useT()
  const playing = seats.filter(s => s.joined)
  const rounds = Math.max(0, ...playing.map(s => s.round_scores?.length ?? 0))
  if (rounds < 2) return null

  const ranked = [...playing].sort((a, b) => (b.score ?? 0) - (a.score ?? 0))

  return (
    <div className="card p-4 mt-4 rounded-2xl border-2" style={{ borderColor: border }}>
      <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted text-center">
        {t('Every round')}
      </p>
      <div className="mt-2.5 space-y-1.5">
        {ranked.map((s, i) => {
          const per = s.round_scores ?? []
          return (
            <div key={s.player}
                 className="flex items-center gap-2 rounded-full border border-brand-border
                            bg-white px-3 py-2">
              <span className="w-5 shrink-0 text-center font-display font-bold text-sm"
                    style={{ color: i === 0 ? accent : '#6B7280' }}>
                {i + 1}
              </span>
              <span className={`shrink-0 max-w-[30%] truncate text-xs font-semibold
                                ${s.skipped ? 'text-brand-muted line-through' : 'text-brand-text'}`}>
                {s.name}{s.player === youPlayer ? ` · ${t('You')}` : ''}
              </span>
              {/* Per-round chips. Scrolls sideways rather than wrapping so
                  every player stays exactly one row tall at 16 seats. */}
              <div className="flex-1 flex items-center gap-1 overflow-x-auto">
                {per.map((n, r) => (
                  <span key={r}
                        className="shrink-0 rounded-full bg-brand-elevated px-2 py-0.5
                                   text-[11px] font-semibold text-brand-muted tabular-nums">
                    {n}
                  </span>
                ))}
              </div>
              <span className="shrink-0 text-brand-muted text-xs">=</span>
              <span className="shrink-0 font-display font-bold text-brand-text tabular-nums">
                {s.score ?? 0}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
